/**
 * Client-side validation for the real-member Ohio Medicaid CERT 270 CSV.
 *
 * PHI-SAFETY CONTRACT: this module parses and validates the CSV entirely in
 * the browser. It NEVER emits, stores, or returns any member value
 * (medicaid_id / name / dob / gender). The result carries COUNTS and, for
 * errors, only a 1-based row number + field name + a generic reason — never
 * the offending value. Nothing here logs, transmits, or persists anything.
 *
 * It intentionally does NOT touch the X12 270 builder, CERT staging,
 * encryption, retention policy, SFTP, or transmission — it only decides
 * whether the operator may proceed to the EXISTING regenerate/stage action.
 */

export const REQUIRED_HEADERS = ['medicaid_id', 'first_name', 'last_name', 'dob', 'gender'];
export const DOB_ALIASES = ['dob', 'date_of_birth'];
/** Server contract (services/cert_real_member_csv.py): EXACTLY these columns. */
export const PERMITTED_HEADERS = ['medicaid_id', 'first_name', 'last_name', 'gender', ...DOB_ALIASES];
/** Synthetic-test metadata — REFUSED in a real-member file (mirrors the server). */
export const REFUSED_METADATA_HEADERS = ['case_id', 'scenario', 'expected_271_response'];
export const ALLOWED_GENDERS = ['M', 'F', 'U'];
export const SYNTHETIC_ID_PREFIXES = ['OH_TEST', 'OHTEST', 'OH-TEST', 'SYNTH', 'TEST'];

export const MIN_INQUIRIES = 50;          // ODM CERT minimum per file
export const MAX_INQUIRIES = 5000;        // matches builder MAX_INQUIRIES_PER_FILE
export const MAX_FILE_BYTES = 2 * 1024 * 1024; // 2 MB — generous for ~5k rows
export const ALLOWED_MIME = ['text/csv', 'application/vnd.ms-excel', 'text/plain', ''];

const MEDICAID_ID_RE = /^\d{12}$/;            // Ohio Medicaid recipient id
const NAME_RE = /^[A-Za-z][A-Za-z '\-.]*$/;   // ASCII letters + real-world punctuation

/** True for built-in synthetic placeholders (OH_TEST_*) or repeated-digit ids. */
export function isSyntheticMemberId(value) {
  const v = String(value ?? '').trim().toUpperCase();
  if (!v) return false;
  if (SYNTHETIC_ID_PREFIXES.some((p) => v.startsWith(p))) return true;
  return MEDICAID_ID_RE.test(v) && /^(\d)\1{11}$/.test(v);
}

/** Strip any path and unsafe chars from an uploaded filename (display only). */
export function sanitizeFilename(name) {
  if (!name) return 'uploaded.csv';
  const base = String(name).split(/[\\/]/).pop();
  const cleaned = base.replace(/[^\w.\- ]+/g, '_').slice(0, 80);
  return cleaned || 'uploaded.csv';
}

/** Guard a File-like object BEFORE reading it. Returns {ok, error}. */
export function checkFile(file) {
  if (!file) return { ok: false, error: 'No file selected.' };
  const name = String(file.name || '');
  const dot = name.lastIndexOf('.');
  const ext = dot >= 0 ? name.slice(dot).toLowerCase() : '';
  if (ext !== '.csv') return { ok: false, error: 'Only .csv files are accepted.' };
  const type = file.type || '';
  if (type && !ALLOWED_MIME.includes(type)) {
    return { ok: false, error: `Unsupported file type. Upload a .csv file.` };
  }
  if (typeof file.size === 'number' && file.size > MAX_FILE_BYTES) {
    return {
      ok: false,
      error: `File too large (${(file.size / 1024 / 1024).toFixed(2)} MB). Max ${(MAX_FILE_BYTES / 1024 / 1024).toFixed(0)} MB.`,
    };
  }
  return { ok: true, error: null };
}

/** Minimal, quote-aware CSV parser. Returns { rows, malformed }. */
export function parseCsvText(text) {
  const s = String(text ?? '');
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < s.length; i += 1) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') { field += '"'; i += 1; } else { inQuotes = false; }
      } else { field += c; }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field); field = '';
    } else if (c === '\n') {
      row.push(field); rows.push(row); row = []; field = '';
    } else if (c === '\r') {
      /* swallow CR (CRLF handled by the following LF) */
    } else {
      field += c;
    }
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return { rows, malformed: inQuotes };
}

/** Accepts YYYY-MM-DD or CCYYMMDD; validates a real calendar date. */
export function isValidDob(value) {
  const v = String(value ?? '').trim();
  let y;
  let m;
  let d;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    [y, m, d] = v.split('-').map(Number);
  } else if (/^\d{8}$/.test(v)) {
    y = Number(v.slice(0, 4)); m = Number(v.slice(4, 6)); d = Number(v.slice(6, 8));
  } else {
    return false;
  }
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return false;
  if (y < 1900 || dt.getTime() > Date.now()) return false;
  return true;
}

/**
 * Validate the CERT CSV + surrounding prerequisites.
 *
 * @param {string} text CSV content
 * @param {object} ctx  { sourceAgency, retentionApproved, storeHealthy, usage }
 * @returns rich result with counts, PHI-safe row errors, and canGenerate.
 */
/**
 * CMS NPI check digit (Luhn over the constant 80840 issuer prefix). Mirrors
 * backend services/npi_validation.py — placeholders such as 0000000000 or
 * 1234567890 fail here even though they are 10 digits.
 */
export function isValidNpi(value) {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!/^[12]\d{9}$/.test(digits)) return false;
  if (/^(\d)\1{9}$/.test(digits)) return false;
  const s = `80840${digits}`;
  let total = 0;
  for (let i = 0; i < s.length; i += 1) {
    let d = Number(s[s.length - 1 - i]);
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    total += d;
  }
  return total % 10 === 0;
}

export function validateCertCsv(text, ctx = {}) {
  const {
    sourceAgency = null,
    retentionApproved = false,
    storeHealthy = false,
    usage = 'T',
  } = ctx;

  const npiDigits = String((sourceAgency && sourceAgency.npi) || '').replace(/\D/g, '');
  const result = {
    hasContent: Boolean(String(text ?? '').trim()),
    malformed: false,
    parsed: false,
    headerValid: false,
    headerErrors: [],
    received: 0,
    valid: 0,
    invalid: 0,
    duplicates: 0,
    emptyRowsIgnored: 0,
    rowErrors: [], // { row, field, message } — NEVER a value
    usageIsCert: usage === 'T',
    agencySelected: Boolean(sourceAgency && sourceAgency.id),
    agencyActive: Boolean(sourceAgency && sourceAgency.status === 'active'),
    agencyNpiValid: isValidNpi(npiDigits),
    retentionApproved: Boolean(retentionApproved),
    storeHealthy: Boolean(storeHealthy),
    minSatisfied: false,
    maxOk: true,
    canGenerate: false,
  };

  if (!result.hasContent) return result;

  const { rows, malformed } = parseCsvText(text);
  result.malformed = malformed;
  if (malformed) {
    result.headerErrors.push('Malformed CSV: an unterminated quoted value was detected.');
  }
  if (!rows.length) {
    result.headerErrors.push('CSV is empty or unparseable.');
    return result;
  }

  const rawHeader = rows[0].map((h) => String(h ?? '').trim().toLowerCase().replace(/^\ufeff/, ''));
  result.parsed = true;
  const dobCols = rawHeader.filter((h) => DOB_ALIASES.includes(h));
  const missingRequired = ['medicaid_id', 'first_name', 'last_name', 'gender']
    .filter((h) => !rawHeader.includes(h));
  if (dobCols.length === 0) missingRequired.push('dob (or date_of_birth)');
  const refused = rawHeader.filter((h) => h && !PERMITTED_HEADERS.includes(h));
  const duplicated = PERMITTED_HEADERS.filter((h) => rawHeader.filter((x) => x === h).length > 1);
  if (missingRequired.length) {
    result.headerErrors.push(`Missing required column(s): ${missingRequired.join(', ')}`);
  }
  if (dobCols.length > 1) {
    result.headerErrors.push('Exactly one DOB column is required (dob or date_of_birth).');
  }
  if (refused.length) {
    const meta = refused.filter((h) => REFUSED_METADATA_HEADERS.includes(h));
    result.headerErrors.push(
      `Refused column(s): ${refused.join(', ')} — a REAL-MEMBER file carries only `
      + `medicaid_id, first_name, last_name, dob, gender${meta.length ? ' (synthetic-test metadata is never accepted)' : ''}.`,
    );
  }
  if (duplicated.length) {
    result.headerErrors.push(`Duplicate column(s): ${duplicated.join(', ')}`);
  }
  result.headerValid = result.headerErrors.length === 0;

  const idx = {};
  PERMITTED_HEADERS.forEach((h) => { idx[h] = rawHeader.indexOf(h); });
  idx.dob = dobCols.length === 1 ? rawHeader.indexOf(dobCols[0]) : -1;
  const get = (cells, h) => (idx[h] >= 0 ? String(cells[idx[h]] ?? '').trim() : '');

  const seen = new Map(); // "medicaid|dob" -> first data-row number
  rows.slice(1).forEach((cells, i) => {
    const rowNum = i + 1; // 1-based DATA row (header excluded)
    const isEmpty = cells.every((c) => String(c ?? '').trim() === '');
    if (isEmpty) { result.emptyRowsIgnored += 1; return; }
    result.received += 1;

    const rowErrs = [];
    if (result.headerValid) {
      const medicaid = get(cells, 'medicaid_id');
      const fn = get(cells, 'first_name');
      const ln = get(cells, 'last_name');
      const dob = get(cells, 'dob');
      const gender = get(cells, 'gender').toUpperCase();

      if (cells.length !== rawHeader.length) {
        rowErrs.push({ row: rowNum, field: 'row', message: 'column count mismatch' });
      }
      if (!medicaid) rowErrs.push({ row: rowNum, field: 'medicaid_id', message: 'missing' });
      else if (isSyntheticMemberId(medicaid)) {
        rowErrs.push({ row: rowNum, field: 'medicaid_id', message: 'synthetic identifier (OH_TEST_* / placeholder) — refused' });
      } else if (!MEDICAID_ID_RE.test(medicaid)) {
        rowErrs.push({ row: rowNum, field: 'medicaid_id', message: 'must be exactly 12 digits' });
      }
      if (!fn) rowErrs.push({ row: rowNum, field: 'first_name', message: 'missing' });
      else if (!NAME_RE.test(fn) || fn.length > 60) rowErrs.push({ row: rowNum, field: 'first_name', message: 'invalid characters' });
      if (!ln) rowErrs.push({ row: rowNum, field: 'last_name', message: 'missing' });
      else if (!NAME_RE.test(ln) || ln.length > 60) rowErrs.push({ row: rowNum, field: 'last_name', message: 'invalid characters' });
      if (!dob) rowErrs.push({ row: rowNum, field: 'dob', message: 'missing' });
      else if (!isValidDob(dob)) rowErrs.push({ row: rowNum, field: 'dob', message: 'invalid date' });
      if (!gender) rowErrs.push({ row: rowNum, field: 'gender', message: 'missing' });
      else if (!ALLOWED_GENDERS.includes(gender)) {
        rowErrs.push({ row: rowNum, field: 'gender', message: 'must be M, F, or U' });
      }

      if (medicaid && !rowErrs.length) {
        const key = `${medicaid.toLowerCase()}|${dob.replace(/-/g, '')}`;
        if (seen.has(key)) {
          result.duplicates += 1;
          rowErrs.push({ row: rowNum, field: 'row', message: `duplicate of row ${seen.get(key)}` });
        } else {
          seen.set(key, rowNum);
        }
      }
    } else {
      // Header is unusable — cannot map fields; flag the whole row.
      rowErrs.push({ row: rowNum, field: 'header', message: 'fix the header row first' });
    }

    if (rowErrs.length) { result.invalid += 1; result.rowErrors.push(...rowErrs); } else { result.valid += 1; }
  });

  result.minSatisfied = result.valid >= MIN_INQUIRIES;
  result.maxOk = result.valid <= MAX_INQUIRIES && result.received <= MAX_INQUIRIES;

  const gatesOk = Boolean(
    result.hasContent
    && result.headerValid
    && !result.malformed
    && result.minSatisfied
    && result.maxOk
    && result.usageIsCert
    && result.agencySelected
    && result.agencyActive
    && result.agencyNpiValid
    && result.retentionApproved
    && result.storeHealthy,
  );
  // Invalid/duplicate rows never block the review by themselves: the server
  // review marks them INVALID — NOT ELIGIBLE FOR CERT and requires explicit
  // exclusion while >= MIN valid unique members remain.
  result.requiresExclusions = result.invalid + result.duplicates > 0;
  result.canGenerateWithExclusions = gatesOk && result.requiresExclusions;
  result.canGenerate = gatesOk && !result.requiresExclusions;

  return result;
}
